import { SolutionOutlined } from '@ant-design/icons';
import { ModuleDefinition } from '../types';
import { createNavItem } from '@/utils/navigation';
import { jobModuleAppointmentExtension } from './extensions/appointmentExtension';
import { jobReminderExtension } from './extensions/reminderExtension';

// Module translations
import en from './locales/en.json';
import de from './locales/de.json';
import bg from './locales/bg.json';

/**
 * Job Module
 *
 * Manages jobs and applications from the external JobManager system.
 * Provides functionality to view jobs, applications, and schedule appointments.
 */

// Module configuration constants
const MODULE_NAME = 'jobModule';
const ROUTE_NAMESPACE = 'manage' as const;

/**
 * Base route path for this module
 * Use this constant throughout the module to avoid duplication
 */
export const MODULE_BASE_ROUTE = `/${ROUTE_NAMESPACE}/modules/${MODULE_NAME}`;

export const module: ModuleDefinition = {
  id: 'job-module',
  name: 'CMS4 Jobs Module',
  version: '1.0.0',
  description: 'Manage jobs and applications from the external JobManager system',
  author: 'System',
  category: 'business',

  // Route namespace - determines where module routes are mounted
  routeNamespace: ROUTE_NAMESPACE,

  // Global roles required
  requiredRoles: ['USER', 'ADMIN'],

  // Tenant roles required
  requiredTenantRoles: ['OWNER', 'EMPLOYEE'],
  useServerRelay: true,

  // Navigation items to add
  navigationItems: [createNavItem('ext-jobs', <SolutionOutlined />, 'CMS4 Jobs Module', MODULE_BASE_ROUTE, false)],

  // Configuration schema
  configSchema: {
    type: 'object',
    properties: {
      apiUrl: {
        type: 'string',
        description: 'The base URL for the JobManager API endpoint',
        required: true,
        serverOnly: true,
      },
      apiToken: {
        type: 'string',
        description: 'The API token for the JobManager API endpoint',
        required: true,
        serverOnly: true,
      },
      rescheduleWebhook: {
        type: 'string',
        description: 'Webhook URL to be called when rescheduling appointments',
        required: true,
        serverOnly: true,
      },
      rescheduleWebhookToken: {
        type: 'string',
        description: 'The API token for the reschedule webhook',
        required: true,
        serverOnly: true,
      }
    }
  },

  extensions: {
    appointments: jobModuleAppointmentExtension,
    reminders: jobReminderExtension
  },

  // Module translations (registered as i18n namespace 'job-module')
  translations: { en, de, bg },

  // Lifecycle hooks
  onLoad: async () => {
    console.log('CMS4 Jobs Module loaded');
  },
};

// Export as default
export default module;
